import type { LocalizedString } from '../schema/localized';

/**
 * Legacy JSON quirk: boolean fields are serialised as `"0"` / `"1"` strings
 * (not actual booleans). Some places also emit real booleans. Normalise both
 * to a real boolean.
 */
export function boolFromStringFlag(v: unknown): boolean {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true';
    return false;
}

/**
 * Coerce a mixed int|string|float to an integer. Returns `def` when the value
 * can't be parsed (null, undefined, NaN, non-numeric string).
 */
export function intFromMixed(v: unknown, def = 0): number {
    if (typeof v === 'number' && Number.isFinite(v)) return Math.trunc(v);
    if (typeof v === 'string' && v.trim().length > 0) {
        const n = Number(v);
        if (Number.isFinite(n)) return Math.trunc(n);
    }
    return def;
}

/**
 * Keep numeric strings as-is (prices are stringified to avoid float drift),
 * but normalise nullish to undefined.
 */
export function numericString(v: unknown): string | undefined {
    if (typeof v === 'string' && v.length > 0) return v;
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    return undefined;
}

/**
 * Generic optional-string coercion: drops empty strings, undefined for nullish.
 */
export function optionalString(v: unknown): string | undefined {
    if (typeof v === 'string' && v.length > 0) return v;
    return undefined;
}

/**
 * Image fields ship in several shapes:
 *   - bare string             "abc.jpg"
 *   - dict                    {filelink: "abc.jpg", original_name?: "..."}
 *   - array (FilePond wrap)   [{filelink: "abc.jpg", ...}] | ["abc.jpg"]
 *   - null / empty array      → undefined
 *
 * Server-side `enrichImageUrls` already swapped filelinks for absolute URLs,
 * so by the time we get here all dict.filelink values are full URLs. We just
 * need to unwrap the legacy container shapes down to a single string.
 *
 * Multi-frame (360°) arrays with several entries collapse to the first frame
 * — callers that need every frame (CombinedImage layer stack) read the raw
 * field through their own parser, not this helper.
 */
export function imageUrlField(v: unknown): string | undefined {
    if (typeof v === 'string') return v.length > 0 ? v : undefined;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
        const fl = (v as Record<string, unknown>).filelink;
        if (typeof fl === 'string' && fl.length > 0) return fl;
        return undefined;
    }
    if (Array.isArray(v)) {
        for (const entry of v) {
            const s = imageUrlField(entry);
            if (s) return s;
        }
    }
    return undefined;
}

/**
 * Coerce a wire-format `{de_DE: "...", ...}` map into a {@link LocalizedString}.
 * Tolerates the field being absent, an empty array (legacy 10289 quirk), or a
 * bare string (rare, treated as untranslated default).
 */
/**
 * The component's default locale while a component is being parsed (see
 * {@link withDefaultLocale}). `localized()` puts that locale first, so
 * pickLocale's last resort — the first non-empty entry — is the default
 * language, as in legacy, and not whichever language was stored first.
 */
let parsingDefaultLocale: string | undefined;

/** Run `parse` with `locale` as the default for every localized string it reads. */
export function withDefaultLocale<T>(locale: string | undefined, parse: () => T): T {
    const previous = parsingDefaultLocale;
    parsingDefaultLocale = locale;
    try {
        return parse();
    } finally {
        parsingDefaultLocale = previous;
    }
}

export function localized(v: unknown): LocalizedString {
    if (v == null) return {};
    if (Array.isArray(v)) return {}; // 10289 quirk: empty array instead of object
    if (typeof v === 'string') {
        // Bare string — treat as locale-less default
        return v.length > 0 ? { '_default_': v } : {};
    }
    if (typeof v !== 'object') return {};

    const out: Record<string, string> = {};
    const preferred = parsingDefaultLocale;
    const record = v as Record<string, unknown>;
    if (preferred && typeof record[preferred] === 'string' && record[preferred] !== '') {
        out[preferred] = record[preferred] as string;
    }
    for (const [k, val] of Object.entries(record)) {
        if (typeof val === 'string' && !(k in out)) out[k] = val;
    }
    return out;
}
